import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { PermissionService } from '../core/services/permission.service';
import { SecureStorageService } from '../core/services/secure-storage.service';
import { LoyaltyAccessService } from '../features/loyalty/services/loyalty-access.service';

/**
 * The Loyalty micro-service gateway (LoyaltyService) requires `msAuthToken`,
 * which legacy only issues on login for super_admin accounts. An admin can
 * still be granted ordinary view permission for loyalty pages
 * (PermissionService.canView) and see the sidebar link, but without this
 * token every loyalty API call fails with a generic "authentication
 * required" error. Send the user to the access-restricted screen instead of
 * letting them land on a page where everything errors out.
 *
 * The destination comes from LoyaltyAccessService, so a user turned away at
 * the door and a user whose token the gateway rejects mid-session get the same
 * explanation.
 */
@Injectable({ providedIn: 'root' })
export class LoyaltyAccessGuard implements CanActivate {
  constructor(
    private secureStorage: SecureStorageService,
    private access: LoyaltyAccessService,
    private permissions: PermissionService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  canActivate(): boolean | UrlTree {
    // On server (SSR), allow navigation — the client will re-check after hydration
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    if (this.secureStorage.getItem('msAuthToken')) {
      return true;
    }

    // No token, two very different reasons — and the signed-in role tells them
    // apart: a super admin should have been issued one at login, so theirs is
    // gone (re-login fixes it); anyone else was never eligible for one, so
    // re-login would change nothing and only a Super Admin can help.
    return this.access.restrictedUrl(
      this.permissions.isSuperAdmin ? 'expired' : 'not-permitted',
    );
  }
}
